import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, Mail, CreditCard, Loader2, Lock, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { formatPrice } from '../utils/formatPrice';
import { trackViewCart, trackRemoveFromCart, trackBeginCheckout } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CartPage() {
  const [cart, setCart] = useState([]);
  const [validatedCart, setValidatedCart] = useState([]); // Cart with validated prices
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [orderForm, setOrderForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderStatus, setOrderStatus] = useState({ type: '', message: '' });
  const [hasInvalidItems, setHasInvalidItems] = useState(false);

  // Load cart and validate prices from database
  // IMPORTANT: Must be defined BEFORE useEffect that uses it
  const loadCart = useCallback(async () => {
    setLoading(true);
    const savedCart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
    setCart(savedCart);
    
    if (savedCart.length === 0) {
      setValidatedCart([]);
      setHasInvalidItems(false);
      setLoading(false);
      return;
    }

    // Validate prices from database
    try {
      const validated = [];
      let hasInvalid = false;

      for (const item of savedCart) {
        try {
          const response = await fetch(`${API_URL}/api/parts/${item.slug}`);
          if (response.ok) {
            const dbProduct = await response.json();
            const dbPrice = dbProduct.price_ttc || 0;
            
            if (dbPrice > 0) {
              validated.push({
                ...item,
                price_ttc: dbPrice, // Use database price
                name: dbProduct.name || item.name,
                image: item.image || (dbProduct.images && dbProduct.images[0]) || null,
                isValid: true
              });
            } else {
              // Product has no valid price
              validated.push({
                ...item,
                price_ttc: 0,
                isValid: false,
                invalidReason: 'Prix en cours de mise à jour'
              });
              hasInvalid = true;
            }
          } else {
            // Product not found
            validated.push({
              ...item,
              price_ttc: 0,
              isValid: false,
              invalidReason: 'Produit indisponible'
            });
            hasInvalid = true;
          }
        } catch (error) {
          validated.push({
            ...item,
            price_ttc: item.price_ttc || 0,
            isValid: (item.price_ttc || 0) > 0,
            invalidReason: item.price_ttc > 0 ? null : 'Prix non vérifié'
          });
          if (!item.price_ttc || item.price_ttc <= 0) hasInvalid = true;
        }
      }

      setValidatedCart(validated);
      setHasInvalidItems(hasInvalid);
    } catch (error) {
      console.error('Error validating cart:', error);
      // Fallback to local data
      const validated = savedCart.map(item => ({
        ...item,
        isValid: (item.price_ttc || 0) > 0,
        invalidReason: item.price_ttc > 0 ? null : 'Prix non disponible'
      }));
      setValidatedCart(validated);
      setHasInvalidItems(validated.some(item => !item.isValid));
      
      // Track GA4 view_cart
      const validItems = validated.filter(item => item.isValid);
      if (validItems.length > 0) {
        const total = validItems.reduce((sum, item) => sum + (item.price_ttc || 0) * (item.quantity || 1), 0);
        trackViewCart(validItems.map(item => ({
          id: item.product_id,
          slug: item.slug,
          name: item.name,
          price: item.price_ttc,
          quantity: item.quantity,
          category: item.product_type
        })), total);
      }
    }
    
    setLoading(false);
  }, []); // stable: only uses state setters and API_URL constant

  // useEffect to load cart on mount and listen for cart updates
  useEffect(() => {
    loadCart();
    
    const handleCartUpdate = () => loadCart();
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, [loadCart]);

  const updateCart = (newCart) => {
    localStorage.setItem('spa_cart', JSON.stringify(newCart));
    setCart(newCart);
    window.dispatchEvent(new Event('cartUpdated'));
    loadCart(); // Re-validate
  };

  const updateQuantity = (slug, delta) => {
    const newCart = cart.map(item => {
      if (item.slug === slug) {
        const newQuantity = Math.max(1, (item.quantity || 1) + delta);
        return { ...item, quantity: newQuantity };
      }
      return item;
    });
    updateCart(newCart);
  };

  const removeItem = (slug) => {
    const itemToRemove = validatedCart.find(item => item.slug === slug);
    const newCart = cart.filter(item => item.slug !== slug);
    updateCart(newCart);
    
    // Track GA4 remove_from_cart
    if (itemToRemove) {
      trackRemoveFromCart({
        id: itemToRemove.product_id,
        slug: itemToRemove.slug,
        name: itemToRemove.name,
        price: itemToRemove.price_ttc,
        category: itemToRemove.product_type
      }, itemToRemove.quantity || 1);
    }
    
    toast.success('Produit retiré du panier');
  };

  const clearCart = () => {
    updateCart([]);
    toast.success('Panier vidé');
  };

  // Calculate total from VALIDATED cart items only
  const calculateTotal = () => {
    return validatedCart
      .filter(item => item.isValid)
      .reduce((sum, item) => {
        const price = parseFloat(item.price_ttc) || 0;
        const quantity = parseInt(item.quantity) || 1;
        return sum + (price * quantity);
      }, 0);
  };

  // Check if cart is valid for checkout
  const canCheckout = validatedCart.length > 0 && 
                      validatedCart.every(item => item.isValid) && 
                      calculateTotal() > 0;

  // Shipping costs configuration
  const SHIPPING_THRESHOLD = 80; // Free shipping threshold in € TTC
  const SHIPPING_COST_HT = 3.50; // Shipping cost HT
  const TVA_RATE = 0.20; // TVA 20%
  const SHIPPING_COST_TTC = SHIPPING_COST_HT * (1 + TVA_RATE); // 4.20€ TTC
  
  // Calculate subtotal (products only)
  const subtotal = calculateTotal();
  
  // Determine if shipping is free
  const shippingFree = subtotal >= SHIPPING_THRESHOLD;
  
  // Calculate shipping cost
  const shippingCost = shippingFree ? 0 : SHIPPING_COST_TTC;
  
  // Calculate final total (products + shipping)
  const total = subtotal + shippingCost;
  
  // Amount remaining for free shipping
  const amountToFreeShipping = Math.max(0, SHIPPING_THRESHOLD - subtotal);

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOrderStatus({ type: 'loading', message: 'Envoi de la commande...' });

    try {
      const res = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: orderForm,
          items: cart.map(item => ({
            slug: item.slug,
            name: item.name,
            sku: item.sku,
            price_ttc: item.price_ttc,
            quantity: item.quantity || 1
          })),
          total_ttc: calculateTotal()
        })
      });

      if (res.ok) {
        setOrderStatus({ type: 'success', message: 'Commande envoyée ! Nous vous contacterons rapidement.' });
        clearCart();
        setShowOrderForm(false);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (err) {
      setOrderStatus({ type: 'error', message: 'Erreur lors de l\'envoi. Veuillez nous contacter par email.' });
    } finally {
      setLoading(false);
    }
  };

  // Stripe Checkout
  const handleStripeCheckout = async () => {
    if (!orderForm.email || !orderForm.name) {
      toast.error('Veuillez remplir votre nom et email');
      return;
    }

    if (!canCheckout) {
      toast.error('Veuillez retirer les produits sans prix du panier');
      return;
    }
    
    setCheckoutLoading(true);
    
    // Track GA4 begin_checkout
    trackBeginCheckout(
      validatedCart.filter(item => item.isValid).map(item => ({
        id: item.product_id || item.slug,
        slug: item.slug,
        name: item.name,
        price: item.price_ttc,
        quantity: item.quantity,
        category: item.product_type
      })),
      total,
      shippingCost
    );
    
    try {
      // Only include valid items with prices
      const cartItems = validatedCart
        .filter(item => item.isValid && item.price_ttc > 0)
        .map(item => ({
          product_id: item.id || item.slug,
          product_type: item.product_type || 'part',
          name: item.name,
          slug: item.slug,
          price: item.price_ttc,
          quantity: item.quantity || 1,
          image: item.image || null
        }));

      if (cartItems.length === 0) {
        toast.error('Aucun produit valide dans le panier');
        setCheckoutLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/payments/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cart_items: cartItems,
          origin_url: window.location.origin,
          customer_email: orderForm.email,
          customer_name: orderForm.name,
          customer_phone: orderForm.phone,
          // Include shipping info
          shipping_cost_ht: shippingFree ? 0 : SHIPPING_COST_HT,
          shipping_cost_ttc: shippingCost,
          shipping_free: shippingFree,
          subtotal: subtotal,
          total: total
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la création du paiement');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Erreur lors du paiement. Veuillez réessayer.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SEO 
        title="Panier | Atlantique Spa Boutique"
        description="Votre panier - Pièces détachées et produits d'entretien pour spa"
      />

      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] flex items-center gap-3">
            <ShoppingCart className="w-8 h-8 text-[#1E3A5F]" />
            Votre panier
          </h1>
          <Link to="/pieces-accessoires" className="text-[#1E3A5F] hover:text-[#F97316] flex items-center gap-2 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            Continuer mes achats
          </Link>
        </div>

        {loading ? (
          /* Loading state */
          <div className="bg-white rounded-2xl p-8 sm:p-12 text-center shadow-sm">
            <Loader2 className="w-12 h-12 text-[#1E3A5F] mx-auto mb-4 animate-spin" />
            <p className="text-[#64748B]">Vérification des prix en cours...</p>
          </div>
        ) : validatedCart.length === 0 ? (
          /* Empty cart */
          <div className="bg-white rounded-2xl p-8 sm:p-12 text-center shadow-sm">
            <ShoppingCart className="w-16 h-16 text-[#CBD5E1] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Votre panier est vide</h2>
            <p className="text-[#64748B] mb-6">Découvrez nos pièces détachées et produits d'entretien pour spa</p>
            <Link to="/pieces-accessoires">
              <Button className="bg-[#1E3A5F] hover:bg-[#152C4A]">
                Voir les produits
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Warning if invalid items */}
            {hasInvalidItems && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Certains produits ne peuvent pas être commandés</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Veuillez retirer les produits marqués en rouge pour procéder au paiement.
                  </p>
                </div>
              </div>
            )}

            {/* Cart items */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {validatedCart.map((item, index) => (
                <div 
                  key={item.slug}
                  className={`flex items-center gap-4 p-4 sm:p-6 ${index > 0 ? 'border-t border-[#E2E8F0]' : ''} ${!item.isValid ? 'bg-red-50' : ''}`}
                  data-testid={`cart-item-${item.slug}`}
                >
                  {/* Image */}
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-[#F8FAFC] rounded-xl flex-shrink-0 overflow-hidden">
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#CBD5E1]">
                        <ShoppingCart className="w-8 h-8" />
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-grow min-w-0">
                    <h3 className="font-semibold text-[#0F172A] text-sm sm:text-base truncate">{item.name}</h3>
                    <p className="text-xs text-[#64748B] mt-1">Réf: {item.sku || 'N/A'}</p>
                    {item.isValid ? (
                      <p className="text-[#F97316] font-bold mt-2">{formatPrice(item.price_ttc)}</p>
                    ) : (
                      <p className="text-red-600 font-medium mt-2 text-sm flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        {item.invalidReason || 'Prix non disponible'}
                      </p>
                    )}
                  </div>

                  {/* Quantity - only show for valid items */}
                  {item.isValid && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.slug, -1)}
                        className="w-8 h-8 rounded-lg bg-[#F8FAFC] hover:bg-[#E2E8F0] flex items-center justify-center transition-colors"
                        data-testid={`qty-minus-${item.slug}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-semibold">{item.quantity || 1}</span>
                      <button
                        onClick={() => updateQuantity(item.slug, 1)}
                        className="w-8 h-8 rounded-lg bg-[#F8FAFC] hover:bg-[#E2E8F0] flex items-center justify-center transition-colors"
                        data-testid={`qty-plus-${item.slug}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Subtotal - only show for valid items */}
                  {item.isValid && (
                    <div className="hidden sm:block text-right min-w-[100px]">
                      <p className="font-bold text-[#0F172A]">{formatPrice(item.price_ttc * (item.quantity || 1))}</p>
                    </div>
                  )}

                  {/* Remove */}
                  <button
                    onClick={() => removeItem(item.slug)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${item.isValid ? 'text-red-500 hover:bg-red-50' : 'text-red-600 bg-red-100 hover:bg-red-200'}`}
                    data-testid={`remove-${item.slug}`}
                    title={item.isValid ? 'Supprimer' : 'Retirer ce produit pour pouvoir commander'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              {/* Subtotal (products only) */}
              <div className="flex justify-between items-center mb-3">
                <span className="text-[#64748B]">
                  Sous-total ({validatedCart.filter(i => i.isValid).reduce((sum, item) => sum + (item.quantity || 1), 0)} article{validatedCart.filter(i => i.isValid).reduce((sum, item) => sum + (item.quantity || 1), 0) > 1 ? 's' : ''})
                </span>
                <span className="font-semibold text-[#0F172A]">{formatPrice(subtotal)}</span>
              </div>
              
              {/* Shipping cost line */}
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-[#E2E8F0]">
                <span className="text-[#64748B]">Frais de livraison</span>
                {shippingFree ? (
                  <span className="text-emerald-600 font-semibold">Offerts</span>
                ) : (
                  <span className="font-semibold text-[#0F172A]">
                    {formatPrice(SHIPPING_COST_HT, false)} HT
                    <span className="text-xs text-[#64748B] ml-1">({formatPrice(SHIPPING_COST_TTC)})</span>
                  </span>
                )}
              </div>
              
              {/* Total */}
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-semibold text-[#0F172A]">Total</span>
                <span className="font-bold text-2xl text-[#0F172A]">{formatPrice(total)}</span>
              </div>
              
              {/* Dynamic free shipping message */}
              {subtotal > 0 && shippingFree ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4" data-testid="free-shipping-banner">
                  <p className="text-emerald-700 font-medium flex items-center gap-2">
                    <span className="text-lg">🎉</span> Livraison offerte !
                  </p>
                </div>
              ) : subtotal > 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="shipping-progress-banner">
                  <p className="text-amber-700 font-medium">
                    Plus que <span className="font-bold">{formatPrice(amountToFreeShipping)}</span> pour bénéficier de la livraison offerte
                  </p>
                  <div className="mt-2 bg-amber-100 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-emerald-500 h-full transition-all duration-300" 
                      style={{ width: `${Math.min(100, (subtotal / SHIPPING_THRESHOLD) * 100)}%` }}
                    />
                  </div>
                </div>
              ) : null}
              
              <p className="text-sm text-[#64748B] mb-6">
                🚚 Livraison France entière – Délai 48-72h selon disponibilité.
              </p>

              {!showOrderForm ? (
                <div className="space-y-3">
                  <Button 
                    onClick={() => setShowOrderForm(true)}
                    disabled={!canCheckout}
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="btn-checkout"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    {canCheckout ? 'Payer par carte' : 'Paiement indisponible'}
                  </Button>
                  {!canCheckout && hasInvalidItems && (
                    <p className="text-red-600 text-sm text-center">
                      Retirez les produits sans prix pour continuer
                    </p>
                  )}
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                    <Lock className="w-4 h-4" />
                    <span>Paiement sécurisé par Stripe</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <Link to="/contact" className="flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-black text-white font-medium py-3 rounded-xl transition-colors">
                      <Mail className="w-4 h-4" />
                      Contact
                    </Link>
                    <button
                      onClick={clearCart}
                      className="text-sm text-red-500 hover:text-red-700 py-3 border border-red-200 rounded-xl hover:bg-red-50 transition-colors"
                    >
                      Vider le panier
                    </button>
                  </div>
                </div>
              ) : (
                /* Checkout form with Stripe */
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-[#0F172A] text-lg">Vos informations</h3>
                    <button 
                      onClick={() => setShowOrderForm(false)}
                      className="text-sm text-slate-500 hover:text-slate-700"
                    >
                      ← Retour
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Nom complet *"
                      required
                      value={orderForm.name}
                      onChange={(e) => setOrderForm({...orderForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                    />
                    <input
                      type="email"
                      placeholder="Email *"
                      required
                      value={orderForm.email}
                      onChange={(e) => setOrderForm({...orderForm, email: e.target.value})}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                    />
                  </div>
                  
                  <input
                    type="tel"
                    placeholder="Téléphone (optionnel)"
                    value={orderForm.phone}
                    onChange={(e) => setOrderForm({...orderForm, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                  />

                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <p className="text-sm text-slate-600">
                      En cliquant sur "Payer", vous serez redirigé vers la page de paiement sécurisée Stripe pour finaliser votre commande.
                    </p>
                  </div>

                  {orderStatus.message && (
                    <div className={`p-4 rounded-xl ${
                      orderStatus.type === 'success' ? 'bg-green-50 text-green-700' :
                      orderStatus.type === 'error' ? 'bg-red-50 text-red-700' :
                      'bg-blue-50 text-blue-700'
                    }`}>
                      {orderStatus.message}
                    </div>
                  )}

                  <Button
                    onClick={handleStripeCheckout}
                    disabled={checkoutLoading || !orderForm.email || !orderForm.name || !canCheckout}
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-6 text-lg disabled:opacity-50"
                    data-testid="btn-pay"
                  >
                    {checkoutLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Redirection vers le paiement...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Payer {formatPrice(total)}
                      </>
                    )}
                  </Button>
                  
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                    <Lock className="w-4 h-4" />
                    <span>Paiement 100% sécurisé par Stripe</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
